import { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Select,
  Switch,
  Upload,
  message,
} from "antd";
import { ArrowLeftOutlined, InboxOutlined } from "@ant-design/icons";
import classNames from "classnames";
import { useNavigate, useSearchParams } from "react-router-dom";
import intl from "react-intl-universal";
import { submitSkillAudit, uploadSkill, uploadSkillVersion } from "@/apis/skillhub";
import styles from "./index.module.scss";

const { Dragger } = Upload;
const getText = (key) => intl.get(`SkillHub.${key}`);

const SkillUpload = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const skillUuid = searchParams.get("id");
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [packageFile, setPackageFile] = useState([]);
  const [iconFile, setIconFile] = useState([]);
  const runtimeOptions = [
    { label: getText("Python"), value: "python" },
    { label: getText("JavaScript"), value: "javascript" },
    { label: getText("Service API"), value: "api" },
    { label: getText("Shell"), value: "shell" },
  ];

  useEffect(() => {
    form.setFieldsValue({
      runtime: "python",
      version: "1.0.0",
      entrypoint: "main.py",
      submitReview: true,
    });
  }, [form]);

  const beforeUploadPackage = (file) => {
    setPackageFile([file]);
    return false;
  };

  const beforeUploadIcon = (file) => {
    setIconFile([file]);
    return false;
  };

  const submit = (values) => {
    if (!packageFile.length) {
      message.warning(getText("Please select a skill package"));
      return;
    }
    const formData = new FormData();

    Object.keys(values).forEach((key) => {
      if (key !== "submitReview" && values[key] !== undefined && values[key] !== null) {
        formData.append(key, values[key]);
      }
    });
    formData.append("packageFile", packageFile[0]);
    if (iconFile.length && !skillUuid) {
      formData.append("iconFile", iconFile[0]);
    }

    setSubmitting(true);
    const request = skillUuid ? uploadSkillVersion(skillUuid, formData) : uploadSkill(formData);
    request
      .then((res) => {
        message.success(skillUuid ? getText("Version uploaded") : getText("Skill uploaded"));
        const nextId = skillUuid || res.data?.skillUuid;
        if (!values.submitReview || !nextId) {
          navigate(nextId ? `/skillhub/detail?id=${nextId}` : "/skillhub");
          return null;
        }
        return submitSkillAudit(nextId, {
          ...(values.version ? { version: values.version } : {}),
          message: getText("Submit for review"),
        })
          .then(() => {
            message.success(getText("Submitted for review"));
          })
          .catch(() => null)
          .finally(() => {
            navigate(`/skillhub/detail?id=${nextId}`);
          });
      })
      .catch(() => {
        message.error(getText("Upload failed"));
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div className={styles.uploadWrap}>
      <div className={classNames("container", styles.uploadPage)}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(skillUuid ? `/skillhub/detail?id=${skillUuid}` : "/skillhub")}>
          {getText("Back")}
        </Button>
        <div className={styles.panel}>
          <div className={styles.header}>
            <p>{skillUuid ? getText("New Skill Version") : getText("Publish Skill")}</p>
            <h1>{skillUuid ? getText("Upload a version package") : getText("Register an agent-ready skill")}</h1>
          </div>

          <Form form={form} layout="vertical" onFinish={submit} className={styles.form}>
            {!skillUuid ? (
              <>
                <Form.Item
                  label={getText("Skill Name")}
                  name="name"
                  rules={[{ required: true, message: getText("Please input skill name") }]}
                >
                  <Input placeholder="climate-data-loader" />
                </Form.Item>
                <Form.Item
                  label={getText("Description")}
                  name="description"
                  rules={[{ required: true, message: getText("Please input description") }]}
                >
                  <Input.TextArea rows={4} placeholder={getText("Description Placeholder")} />
                </Form.Item>
              </>
            ) : null}

            <div className={styles.twoColumns}>
              <Form.Item label={getText("Version")} name="version">
                <Input placeholder="1.0.0" />
              </Form.Item>
              <Form.Item label={getText("Runtime")} name="runtime">
                <Select options={runtimeOptions} />
              </Form.Item>
            </div>

            <Form.Item label={getText("Entrypoint")} name="entrypoint">
              <Input placeholder={getText("Entrypoint Placeholder")} />
            </Form.Item>

            <Form.Item label={getText("Service API")} name="serviceApi">
              <Input placeholder={getText("Service API Placeholder")} />
            </Form.Item>
            <Form.Item label={getText("Tags")} name="tags">
              <Input placeholder={getText("Tags Placeholder")} />
            </Form.Item>

            {skillUuid ? (
              <>
                <Form.Item label={getText("Set as default version")} name="setDefault" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </>
            ) : null}

            <Form.Item label={getText("Submit after upload")} name="submitReview" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Form.Item label={getText("Skill Package")} required>
              <Dragger
                maxCount={1}
                fileList={packageFile}
                beforeUpload={beforeUploadPackage}
                onRemove={() => setPackageFile([])}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">{getText("Click or drag a package file here")}</p>
                <p className="ant-upload-hint">{getText("Package Hint")}</p>
              </Dragger>
            </Form.Item>

            {!skillUuid ? (
              <Form.Item label={getText("Icon")}>
                <Upload
                  maxCount={1}
                  listType="picture"
                  fileList={iconFile}
                  beforeUpload={beforeUploadIcon}
                  onRemove={() => setIconFile([])}
                >
                  <Button>{getText("Select Icon")}</Button>
                </Upload>
              </Form.Item>
            ) : null}

            <div className={styles.actions}>
              <Button onClick={() => navigate(skillUuid ? `/skillhub/detail?id=${skillUuid}` : "/skillhub")}>
                {getText("Cancel")}
              </Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {skillUuid ? getText("Upload Version") : getText("Upload Skill")}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default SkillUpload;
